import { Head, router, usePage } from '@inertiajs/react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import LanguageIcon from '@mui/icons-material/Language'
import StorageIcon from '@mui/icons-material/Storage'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import AppLayout from '../../components/layouts/AppLayout'
import ConfigGenerator from '../../components/storefront/ConfigGenerator'
import type { SharedProps, StorefrontEntryDetail, StorefrontRelease, StorefrontPackage } from '../../types'

interface Props {
  entry: StorefrontEntryDetail
  releases: StorefrontRelease[]
  packages: StorefrontPackage[]
  seo: { title: string; description?: string | null; ogImage?: string | null }
  following: boolean
  followersCount: number
}

export default function Show({ entry, releases, packages, seo, following: initialFollowing, followersCount: initialCount }: Props) {
  const { props } = usePage<SharedProps>()
  const { current_user } = props
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isFollowing, setIsFollowing] = useState(initialFollowing)
  const [followersCount, setFollowersCount] = useState(initialCount)
  const [followLoading, setFollowLoading] = useState(false)

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const handleFollowToggle = async () => {
    if (!current_user) {
      router.visit('/users/sign_in')
      return
    }

    setFollowLoading(true)
    const method = isFollowing ? 'DELETE' : 'POST'
    const url = `/app_entries/${entry.id}/follow`

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
          'Accept': 'application/json',
        },
      })
      const data = await response.json()
      setIsFollowing(data.following)
      setFollowersCount(data.followersCount)
    } finally {
      setFollowLoading(false)
    }
  }

  return (
    <AppLayout>
      <Head title={seo.title}>
        {seo.description && <meta name="description" content={seo.description} />}
        <meta property="og:title" content={seo.title} />
        {seo.description && <meta property="og:description" content={seo.description} />}
        {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: entry.name,
          description: entry.shortDescription || entry.description,
          applicationCategory: entry.categories.map(c => c.name).join(', '),
          softwareVersion: entry.latestVersion,
          image: entry.logoUrl,
          author: { '@type': 'Organization', name: entry.vendorName },
        })}</script>
      </Head>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/apps" onClick={(e) => { e.preventDefault(); router.visit('/apps') }}>
            Apps
          </Link>
          <Typography color="text.primary">{entry.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4, gap: 3 }}>
          {entry.logoUrl ? (
            <Box
              component="img"
              src={entry.logoUrl}
              alt={entry.name}
              sx={{ width: 96, height: 96, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <Box sx={{
              width: 96, height: 96, borderRadius: 2, flexShrink: 0,
              bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AppsIcon sx={{ fontSize: 48, color: 'grey.500' }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4">{entry.name}</Typography>
              <Button
                variant={isFollowing ? 'contained' : 'outlined'}
                size="small"
                startIcon={isFollowing ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                onClick={handleFollowToggle}
                disabled={followLoading}
                data-testid="follow-button"
              >
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
              <Typography variant="body2" color="text.secondary" data-testid="followers-count">
                {followersCount} {followersCount === 1 ? 'follower' : 'followers'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body1" color="text.secondary">
                by {entry.vendorName}
              </Typography>
              {entry.vendorWebsite && (
                <IconButton size="small" href={entry.vendorWebsite} target="_blank" rel="noopener">
                  <LanguageIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {entry.categories.map((cat) => (
                <Chip
                  key={cat.slug}
                  label={cat.name}
                  size="small"
                  onClick={() => router.visit(`/apps?category=${cat.slug}`)}
                />
              ))}
              {entry.latestVersion && (
                <Chip label={`v${entry.latestVersion}`} size="small" color="primary" />
              )}
            </Box>
            {entry.shortDescription && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {entry.shortDescription}
              </Typography>
            )}
          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Description */}
            {entry.description && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Box sx={{ '& img': { maxWidth: '100%' }, '& a': { color: 'primary.main' } }}>
                    <ReactMarkdown>{entry.description}</ReactMarkdown>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Screenshots */}
            {entry.screenshots.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Screenshots</Typography>
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                    {entry.screenshots.map((ss, i) => (
                      <Box
                        key={i}
                        component="img"
                        src={ss.thumbnailUrl}
                        alt={`${entry.name} screenshot ${i + 1}`}
                        onClick={() => openLightbox(i)}
                        sx={{
                          height: 160, borderRadius: 1, cursor: 'pointer', flexShrink: 0,
                          '&:hover': { opacity: 0.8 },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Config Generator */}
            {packages.length > 0 && (
              <ConfigGenerator
                packages={packages}
                vendorSlug={entry.vendorSlug}
                entrySlug={entry.slug}
              />
            )}

            {/* Version History */}
            {releases.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Version History</Typography>
                  {releases.map((release) => (
                    <Accordion key={release.id} disableGutters elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            v{release.version}
                          </Typography>
                          {release.publishedAt && (
                            <Typography variant="body2" color="text.secondary">
                              {new Date(release.publishedAt).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {release.changelog ? (
                          <Box sx={{ '& a': { color: 'primary.main' } }}>
                            <ReactMarkdown>{release.changelog}</ReactMarkdown>
                          </Box>
                        ) : (
                          <Typography color="text.secondary">No changelog available.</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            {packages.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Deployment Options</Typography>
                  <List dense disablePadding>
                    {packages.map((pkg) => (
                      <ListItem key={pkg.id} disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <StorageIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={pkg.name}
                          secondary={pkg.deploymentTypeLabel + (pkg.variantLabel ? ` (${pkg.variantLabel})` : '')}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Lightbox */}
        <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg">
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
            {entry.screenshots[lightboxIndex] && (
              <Box
                component="img"
                src={entry.screenshots[lightboxIndex].largeUrl}
                alt={`${entry.name} screenshot`}
                sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }}
              />
            )}
          </Box>
        </Dialog>
      </Container>
    </AppLayout>
  )
}
